import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@context/AuthContext';
import Button from '@components/Button/Button';
import Input from '@components/Form/Input/Input';
import type { StudentLoginPayload } from '@/services/auth/types';
import { authService } from '@/services/auth/authService';
import { Images } from '@assets';

const StudentLoginPage: React.FC = () => {
    const { t } = useTranslation('students');
    const { register, handleSubmit, formState: { errors } } = useForm<StudentLoginPayload>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [serverError, setServerError] = useState('');
    const navigate = useNavigate();
    const { login } = useAuth();

    const onSubmit = async (data: StudentLoginPayload) => {
        try {
            setIsLoading(true);
            setServerError('');
            const trimmedData = {
                enrollment_number: data.enrollment_number?.trim() || '',
                password: data.password?.trim() || '',
            };
            const response = await authService.studentLogin(trimmedData);
            
            await login(response.data.data.access_token);

            if (response.data.data.user.force_password_change) {
                navigate('/student/change-password');
            } else {
                navigate('/student/dashboard');
            }
        } catch (error: any) {
            setServerError(error?.response?.data?.message || t('portal.login.invalidCreds'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-blue-bg flex items-center justify-center p-4 relative overflow-hidden">
            {/* Top Branding Logos */}
            <div className="absolute top-4 left-4 md:top-8 md:left-8 z-10">
                <img src={Images.tpaLogo} alt="TPA Logo" className="h-8 md:h-11 w-auto object-contain invert brightness-90 hue-rotate-[220deg]" />
            </div>
            
            <div className="absolute top-4 right-4 md:top-8 md:right-8 z-10">
                <img src={Images.onembillion} alt="1M1B Logo" className="h-8 md:h-11 w-auto object-contain" />
            </div>

            <div className="bg-white rounded-2xl shadow-card w-full max-w-md p-8 relative z-20">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-navy mb-2">{t('portal.login.title')}</h1>
                    <p className="text-slate-body">{t('portal.login.subtitle')}</p>
                </div>

                {serverError && (
                    <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-sm text-center">
                        {serverError}
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <Input
                        label={t('portal.login.enrollmentLabel', { defaultValue: 'Enrollment Number' })}
                        type="text"
                        placeholder={t('portal.login.enrollmentPlaceholder', { defaultValue: 'Enter your enrollment number (e.g. 26-Jul-001)' })}
                        registration={register('enrollment_number', { required: t('portal.login.enrollmentRequired', { defaultValue: 'Enrollment Number is required' }) })}
                        error={errors.enrollment_number?.message}
                        className="!py-3 !rounded-lg"
                    />

                    <Input
                        label={t('portal.login.passwordLabel')}
                        type="password"
                        placeholder={t('portal.login.passwordPlaceholder')}
                        registration={register('password', { required: t('portal.login.passwordRequired') })}
                        error={errors.password?.message}
                        className="!py-3 !rounded-lg"
                    />

                    <Button
                        type="submit"
                        variant="primary"
                        className="w-full !py-3 !rounded-full hover:-translate-y-0.5 transition-all duration-300"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Signing in...' : t('portal.login.signInBtn')}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default StudentLoginPage;
